import { Component } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import ReactDOM from 'react-dom';

import get from 'lodash.get';
import classNames from 'classnames';

import { Link } from 'dibs-elements/exports/Link';

import { shouldRenderContactModal } from '../helpers/addressPreferences';
import { ABOUT_CHAR_LIMIT, ABOUT_CHAR_LIMIT_MOBILE } from '../helpers/constants';

import StorefrontSectionHeader from './StorefrontSectionHeader';
import StorefrontContactModal from '../contactModal/StorefrontContactModal';

import styles from './styles/StorefrontAboutSection.scss';

function addEllipsis(text, charLimit) {
    if (typeof text !== 'string') {
        return '';
    }
    if (typeof charLimit !== 'number' || text.length <= charLimit) {
        return text;
    }
    charLimit = Math.max(charLimit - 3, 0);
    return `${text.slice(0, charLimit)}...`;
}

class StorefrontAboutSection extends Component {
    constructor(props) {
        super(props);

        this.state = {
            charLimit: ABOUT_CHAR_LIMIT,
            showAll: false,
            isContactModalOpen: false,
        };

        this._updateAboutCharLimit = this._updateAboutCharLimit.bind(this);
        this._updateHeight = this._updateHeight.bind(this);
        this._onClickReadMore = this._onClickReadMore.bind(this);
        this._onClickContactInfo = this._onClickContactInfo.bind(this);
    }

    componentDidMount() {
        this._updateAboutCharLimit();
        window.addEventListener('resize', this._updateAboutCharLimit);
        if (get(this.props, 'seller.storefrontProfile.aboutUs')) {
            window.addEventListener('resize', this._updateHeight);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._updateHeight);
        window.removeEventListener('resize', this._updateAboutCharLimit);
    }

    /**
     * Calculates and updates the number of about us text characters that should show
     */
    _updateAboutCharLimit() {
        const winW = window.innerWidth;
        if (!winW) {
            return;
        }

        const newCharLimit = winW < 567 ? ABOUT_CHAR_LIMIT_MOBILE : ABOUT_CHAR_LIMIT;
        if (newCharLimit !== this.state.charLimit) {
            this.setState({ charLimit: newCharLimit });
        }
    }

    _updateHeight() {
        const sectionEl = ReactDOM.findDOMNode(this);
        const aboutEl = sectionEl.getElementsByClassName(styles.about)[0];
        const aboutUsEl = sectionEl.getElementsByClassName(styles.aboutUs)[0];

        if (aboutEl && aboutUsEl) {
            // Adjust height to smooth transition
            aboutEl.style.height = aboutUsEl.offsetHeight + 'px';
        }
    }

    _onClickReadMore(e) {
        e.preventDefault();
        this.setState(state => ({ showAll: !state.showAll }), this._updateHeight);
    }

    _onClickContactInfo(e) {
        e.preventDefault();
        this.setState({ isContactModalOpen: true });
    }

    _getSellerLogo() {
        const sellerLogo = get(this.props, 'seller.storefrontProfile.logo.path');
        const scrollLogo = get(this.props, 'seller.sellerPreferences.scrollLogoLg');
        return sellerLogo || scrollLogo; // v2 versus v1 logos
    }

    renderDealerAbout() {
        const charLimit = this.state.charLimit;

        let link = 'Read Less';
        let aboutUs = get(this.props, 'seller.storefrontProfile.aboutUs');

        if (aboutUs.length < charLimit) {
            link = null;
        } else if (!this.state.showAll) {
            link = 'Read More';
            aboutUs = addEllipsis(aboutUs, charLimit);
        }

        return (
            <div className={styles.about}>
                <p className={styles.aboutUs}>
                    {aboutUs}
                    <span
                        onClick={this._onClickReadMore}
                        onKeyPress={this._onClickReadMore}
                        role="button"
                        tabIndex={-1}
                        className={styles.readMoreLess}
                    >
                        {link}
                    </span>
                </p>
            </div>
        );
    }

    renderDealerInfo() {
        const logo = this._getSellerLogo();
        const seller = this.props?.seller;
        const company = seller?.sellerProfile?.company;
        const establishmentYear = get(this.props, 'seller.storefrontProfile.establishmentYear');

        return (
            <div className={styles.info}>
                {/* Dealer Logo */}
                {logo ? (
                    <img
                        data-tn="storefront-about-section-image"
                        className={styles.logo}
                        src={logo}
                        alt={company}
                    />
                ) : (
                    <p className={styles.logo}>{company}</p>
                )}

                <p>
                    {/* Established in {YEAR} */}
                    {establishmentYear && (
                        <span
                            data-tn="storefront-about-section-establishmentYear"
                            className={styles.yearJoined}
                        >
                            <FormattedMessage
                                id="StorefrontAboutSection.establishmentYear"
                                defaultMessage="Established in {establishmentYear}"
                                values={{ establishmentYear }}
                            />
                            <span className={styles.divider} />
                        </span>
                    )}
                    {/* Joined 1stDibs {YEAR} */}
                    <span
                        data-tn="storefront-about-section-dealer-since"
                        className={styles.yearJoined}
                    >
                        <FormattedMessage
                            id="StorefrontAboutSection.yearJoined"
                            defaultMessage="1stDibs seller since {year}"
                            values={{ year: get(this.props, 'sellerData.dealerSince') }}
                        />
                    </span>
                </p>

                {/* Associations */}
                {get(this.props, 'seller.tradeAssociations.length') ? (
                    <div>
                        <p
                            data-tn="storefront-about-section-associations"
                            className={styles.associations}
                        >
                            <FormattedMessage
                                id="StorefrontAboutSection.associations"
                                defaultMessage="Associations"
                            />
                        </p>

                        {get(this.props, 'seller.tradeAssociations').map((association, index) => {
                            const key = `${get(association, 'displayName')}${index}`;

                            return (
                                <p key={key} className={styles.association}>
                                    {get(association, 'displayName')}
                                </p>
                            );
                        })}
                    </div>
                ) : null}

                {shouldRenderContactModal(this.props.seller) ? (
                    <Link
                        dataTn="storefront-about-section-contact"
                        className={styles.contact}
                        href="#0"
                        onClick={this._onClickContactInfo}
                    >
                        <FormattedMessage
                            id="StorefrontAboutSection.contactCta"
                            defaultMessage="Contact Info"
                        />
                    </Link>
                ) : null}
            </div>
        );
    }

    render() {
        // minimal layout is for dealers who don't have an aboutUs text
        const minLayout = !get(this.props, 'seller.storefrontProfile.aboutUs');
        const className = classNames(styles.section, {
            [styles.minimal]: minLayout,
        });

        return (
            <section className={className}>
                {/* Header (About {Dealer Name} */}
                {!minLayout ? (
                    <StorefrontSectionHeader
                        header={
                            <FormattedMessage
                                id="StorefrontAboutSection.header"
                                defaultMessage="About {company}"
                                values={{
                                    company: get(this.props, 'seller.sellerProfile.company'),
                                }}
                            />
                        }
                    />
                ) : null}

                {/* Content */}
                <div className={styles.content}>
                    {!minLayout ? (
                        <div className="rowFlex">
                            {/* Left */}
                            <div className={`colLg9 colMd8 colXs12`}>
                                {this.renderDealerAbout()}
                            </div>

                            {/* Right */}
                            <div className={`colLg3 colMd4 colXs12`}>{this.renderDealerInfo()}</div>
                        </div>
                    ) : (
                        this.renderDealerInfo()
                    )}
                </div>

                <StorefrontContactModal
                    seller={this.props.seller}
                    isOpen={this.state.isContactModalOpen}
                    closeModal={() => this.setState({ isContactModalOpen: false })}
                />
            </section>
        );
    }
}

StorefrontAboutSection.propTypes = {
    sellerData: PropTypes.shape({
        dealerSince: PropTypes.string.isRequired,
    }).isRequired,
    seller: PropTypes.object.isRequired,
};

export default createFragmentContainer(StorefrontAboutSection, {
    seller: graphql`
        fragment StorefrontAboutSection_seller on Seller {
            ...StorefrontContactModal_seller
            storefrontProfile(storefrontId: $storefrontId) {
                aboutUs
                logo {
                    path
                }
                establishmentYear
            }
            sellerProfile {
                company
            }
            sellerPreferences {
                scrollLogoLg
                displayStoreAddress
                storeAddressByAppointmentOnly
            }
            tradeAssociations {
                displayName
            }
        }
    `,
});
